import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

const dummyObject = new THREE.Object3D()

export default class Smoke {
  scene: THREE.Object3D
  debugFolder?: FolderApi
  mesh!: THREE.InstancedMesh
  smokeTexture: THREE.Texture

  rotations: number[] = []
  positions: THREE.Vector3[] = []
  axisAlignment: "x" | "y" | "z" = "x"

  settings = {
    color: "#344569",
    speed: 1.1,
    count: 5,
    opacity: 0.2,
    x: -15,
    y: -8,
    z: -14,
  }

  constructor({
    scene,
    debugFolder,
    smokeTexture,
    axisAlignment = "x",
  }: {
    scene: THREE.Object3D
    smokeTexture: THREE.Texture
    debugFolder?: FolderApi
    axisAlignment?: "x" | "y" | "z"
  }) {
    this.smokeTexture = smokeTexture
    this.scene = scene
    this.debugFolder = debugFolder

    this.axisAlignment = axisAlignment

    this.setMesh()

    this.setDebug()
  }

  setMesh() {
    const geometry = new THREE.PlaneGeometry(30, 30)

    const material = new THREE.MeshBasicMaterial({
      map: this.smokeTexture,
      transparent: true,
      opacity: this.settings.opacity,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      premultipliedAlpha: true,
      color: new THREE.Color(this.settings.color),
    })

    this.mesh = new THREE.InstancedMesh(geometry, material, this.settings.count)

    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage) // will be updated every frame
    this.scene.add(this.mesh)

    for (let i = 0; i < this.settings.count; i++) {
      // Store position and rotation for this instance
      // Position distribution varies based on axis alignment
      let x: number
      let z: number

      if (this.axisAlignment === "y") {
        // When facing X axis: same X position, spread on Z axis
        x = this.settings.x
        z = Math.random() * 50 - 25
      } else {
        // Default: spread on X axis, same Z range
        x = Math.random() * 50 - 25 // -25 to 25
        z = this.settings.z
      }

      const position = new THREE.Vector3(x, this.settings.y, z)
      this.positions[i] = position
      this.rotations[i] = Math.random() * Math.PI * 2

      dummyObject.position.copy(position)

      // Apply base rotation based on axis alignment
      // PlaneGeometry is in XY plane (facing +Z) by default
      if (this.axisAlignment === "x") {
        // Face Z axis → no rotation needed (default orientation)
        // No rotation applied
      } else if (this.axisAlignment === "y") {
        // Face X axis → rotate 90° around Y axis
        dummyObject.rotation.y = Math.PI / 2
      }
      // "z" alignment: no base rotation needed (default XY plane orientation)

      // Apply animation rotation around Z axis
      dummyObject.rotation.z = this.rotations[i]
      dummyObject.updateMatrix()

      this.mesh.setMatrixAt(i, dummyObject.matrix)
    }
  }

  update() {
    for (let i = 0; i < this.settings.count; i++) {
      // Update rotation directly without matrix decomposition
      this.rotations[i] -= this.settings.speed * 0.001

      // Rebuild matrix from stored position and updated rotation
      dummyObject.position.copy(this.positions[i])

      // Reset base rotations
      dummyObject.rotation.x = 0
      dummyObject.rotation.y = 0

      // Apply base rotation based on axis alignment
      if (this.axisAlignment === "x") {
        // Face Z axis → no rotation needed
        // No rotation applied
      } else if (this.axisAlignment === "y") {
        // Face X axis → rotate 90° around Y axis
        dummyObject.rotation.y = Math.PI / 2
      }

      // Apply animation rotation around Z axis
      dummyObject.rotation.z = this.rotations[i]
      dummyObject.updateMatrix()

      this.mesh.setMatrixAt(i, dummyObject.matrix)
    }
    this.mesh.instanceMatrix.needsUpdate = true
  }

  setDebug() {
    if (this.debugFolder) {
      const folder = this.debugFolder.addFolder({
        title: `☁ Smoke (${this.axisAlignment})`,
        expanded: false,
      })

      folder
        .addBinding(this.settings, "color", {
          view: "color",
        })
        .on("change", (ev) => {
          const material = this.mesh.material as THREE.MeshBasicMaterial
          material.color = new THREE.Color(ev.value)
        })

      folder.addBinding(this.settings, "speed", {
        min: 0,
        max: 2,
        step: 0.01,
      })

      folder
        .addBinding(this.settings, "opacity", {
          min: 0,
          max: 0.5,
          step: 0.001,
        })
        .on("change", (ev) => {
          const material = this.mesh.material as THREE.MeshBasicMaterial
          material.opacity = ev.value
        })
      folder
        .addBinding(this.settings, "x", {
          min: -15,
          max: -3,
          step: 0.001,
        })
        .on("change", (ev) => {
          this.mesh.position.x = ev.value
        })
      folder
        .addBinding(this.settings, "y", {
          min: -10,
          max: 3,
          step: 0.001,
        })
        .on("change", (ev) => {
          this.mesh.position.y = ev.value
        })

      folder
        .addBinding(this.settings, "z", {
          min: -15,
          max: -3,
          step: 0.001,
        })
        .on("change", (ev) => {
          this.mesh.position.z = ev.value
        })
    }
  }
}
